/**
 * fleet-panel.ts — v3M16.2 Fleet roster panel.
 *
 * Left-side fixed-position panel showing all 10 fleet (control-group)
 * slots. Each row surfaces:
 *   - The slot's hotkey indicator `[N]`.
 *   - A count + chassis breakdown when the slot has live members
 *     (e.g. "3 · 2× Cruiser, 1× Scout").
 *   - A dim em-dash for empty slots so the player can see at a glance
 *     which numbers are free.
 *
 * Click-to-recall: pointerdown on a row fires `recallFleet` against
 * the live state. The currently-active fleet (when `selectedShipIds`
 * exactly matches the slot's live roster) is visually highlighted so
 * the player can confirm what they have selected.
 *
 * Mount pattern mirrors `dev-toolbox.ts`: panel created once at session
 * start, `update(state, ui)` called per frame to refresh row contents,
 * `destroy()` on session teardown. Strictly UI state — never on the
 * command stream, never serialised. Per the existing posture, fleets
 * reset on session restart.
 */

import type { GameState } from '../state/types';
import type { UiState } from './state';
import { FLEET_IDS, getFleetRoster, recallFleet } from './state';

export interface FleetPanelHandle {
  update(state: GameState, ui: UiState): void;
  destroy(): void;
}

export function mountFleetPanel(parent: HTMLElement): FleetPanelHandle {
  // ── panel root ────────────────────────────────────────────────────────
  const panel = document.createElement('div');
  Object.assign(panel.style, {
    position: 'fixed',
    top: '50%',
    left: '12px',
    transform: 'translateY(-50%)',
    width: '220px',
    background: 'rgba(6,10,20,0.82)',
    border: '1px solid rgba(80,110,160,0.4)',
    borderRadius: '4px',
    padding: '8px 10px',
    color: '#d8e4ff',
    fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    fontSize: '11px',
    lineHeight: '1.4',
    fontVariantNumeric: 'tabular-nums',
    zIndex: '700',
    pointerEvents: 'auto',
  } as Partial<CSSStyleDeclaration>);

  // ── header ────────────────────────────────────────────────────────────
  const header = document.createElement('div');
  header.textContent = 'FLEETS';
  Object.assign(header.style, {
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '0.12em',
    color: '#8ea0c8',
    marginBottom: '4px',
    textTransform: 'uppercase',
  } as Partial<CSSStyleDeclaration>);
  panel.appendChild(header);

  const hint = document.createElement('div');
  hint.textContent = 'Ctrl+N assign · N recall';
  Object.assign(hint.style, {
    fontSize: '10px',
    color: '#5d6b85',
    marginBottom: '8px',
  } as Partial<CSSStyleDeclaration>);
  panel.appendChild(hint);

  // ── 10 fleet rows (slots 0..9) ────────────────────────────────────────
  // Closure-captured handlers read the latest (state, ui) snapshot from
  // the per-frame `update` call. Without this indirection, the click
  // handler would close over a stale `state` reference from session
  // start and never see new ships.
  let liveState: GameState | null = null;
  let liveUi: UiState | null = null;

  interface Row {
    row: HTMLDivElement;
    content: HTMLSpanElement;
  }
  const rows: Row[] = [];

  for (const id of FLEET_IDS) {
    const row = document.createElement('div');
    Object.assign(row.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '3px 6px',
      cursor: 'pointer',
      borderRadius: '2px',
      userSelect: 'none',
      transition: 'background 80ms ease-out',
    } as Partial<CSSStyleDeclaration>);

    const label = document.createElement('span');
    label.textContent = `[${id}]`;
    Object.assign(label.style, {
      fontWeight: '600',
      color: '#8ea0c8',
      minWidth: '24px',
    } as Partial<CSSStyleDeclaration>);

    const content = document.createElement('span');
    Object.assign(content.style, {
      flex: '1',
      color: '#a8b6d0',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    } as Partial<CSSStyleDeclaration>);

    row.appendChild(label);
    row.appendChild(content);

    // pointerdown to dodge the per-frame DOM rebuild race noted in
    // detail-panel.ts and the dev-toolbox grant buttons.
    row.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      if (liveState !== null && liveUi !== null) {
        recallFleet(liveUi, liveState, id);
      }
    });

    panel.appendChild(row);
    rows.push({ row, content });
  }

  parent.appendChild(panel);

  // ── update / destroy ──────────────────────────────────────────────────
  function update(state: GameState, ui: UiState): void {
    liveState = state;
    liveUi = ui;
    for (let i = 0; i < FLEET_IDS.length; i++) {
      const fleetId = FLEET_IDS[i]!;
      const r = rows[i]!;
      const roster = getFleetRoster(ui, state, fleetId);

      if (roster.length === 0) {
        r.content.textContent = '—';
        r.content.style.color = '#4a5470';
        r.row.style.background = 'transparent';
        continue;
      }

      // Chassis breakdown — count by chassis name, preserve insertion
      // order so a fleet of [Scout, Cruiser, Cruiser] reads as
      // "Scout, 2× Cruiser" rather than alphabetised.
      const counts = new Map<string, number>();
      for (const ship of roster) {
        counts.set(ship.chassis, (counts.get(ship.chassis) ?? 0) + 1);
      }
      const breakdown = [...counts.entries()]
        .map(([chassis, n]) => (n > 1 ? `${n}× ${chassis}` : chassis))
        .join(', ');
      r.content.textContent = `${roster.length} · ${breakdown}`;
      r.content.style.color = '#d8e4ff';

      // Highlight when the active selection exactly matches the slot's
      // live roster — covers "I just recalled this fleet" plus the
      // case where some members died (selection + roster shrink in
      // lockstep so they still match).
      const matches = selectionMatchesRoster(ui, roster);
      r.row.style.background = matches
        ? 'rgba(110, 160, 220, 0.20)'
        : 'transparent';
    }
  }

  function destroy(): void {
    panel.remove();
  }

  return { update, destroy };
}

/**
 * True iff `ui.selectedShipIds` is exactly the set of ids in the given
 * live roster (same size + same membership). Used by the panel
 * highlight rule.
 */
function selectionMatchesRoster(
  ui: UiState,
  roster: ReadonlyArray<{ id: number }>,
): boolean {
  if (ui.selectedShipIds.size !== roster.length) return false;
  for (const s of roster) {
    if (!ui.selectedShipIds.has(s.id)) return false;
  }
  return true;
}
